import { Divider, Link, ListItemText, Menu, MenuItem } from '@mui/material'
import { useId, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import ChevronDownIcon from '@/icons/ChevronDown'
import {
    FEATURES,
    FEATURES_PATH,
    featureHref
} from '@/pages/marketing/features/registry.js'

/**
 * The navbar "Features" dropdown: an overview link plus one deep link per capability section of
 * /features. Click-to-open rather than hover-to-open, so it works the same on a touch screen and
 * for a keyboard — MUI's Menu handles arrow keys, Escape and focus return for us.
 *
 * The mobile sidebar carries the same list as a plain expanded group; both read FEATURES, so
 * they cannot drift apart.
 */
const FeaturesMenu = () => {
    const [anchorEl, setAnchorEl] = useState(null)
    const open = Boolean(anchorEl)
    // The button and the menu reference each other for screen readers, so both need ids — and
    // generated ones, since nothing stops a second instance of this component appearing.
    const buttonId = useId()
    const menuId = useId()

    const handleClose = () => setAnchorEl(null)

    return (
        <>
            <Link
                aria-controls={open ? menuId : undefined}
                aria-expanded={open}
                aria-haspopup="true"
                component="button"
                color="textSecondary"
                id={buttonId}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: 0.25,
                    '&:hover': { color: 'primary.main' }
                }}
                underline="none"
                variant="body2"
            >
                Features
                <ChevronDownIcon fontSize="small" />
            </Link>

            <Menu
                anchorEl={anchorEl}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                id={menuId}
                onClose={handleClose}
                open={open}
                slotProps={{
                    list: { 'aria-labelledby': buttonId },
                    paper: { sx: { maxWidth: 340, mt: 1 } }
                }}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            >
                <MenuItem
                    component={RouterLink}
                    onClick={handleClose}
                    to={FEATURES_PATH}
                >
                    <ListItemText
                        primary="All features"
                        slotProps={{ primary: { variant: 'subtitle2' } }}
                    />
                </MenuItem>

                <Divider />

                {FEATURES.map((feature) => (
                    <MenuItem
                        key={feature.key}
                        component={RouterLink}
                        onClick={handleClose}
                        sx={{ py: 1, whiteSpace: 'normal' }}
                        to={featureHref(feature.key)}
                    >
                        <ListItemText
                            primary={feature.navTitle}
                            secondary={feature.navBlurb}
                            slotProps={{
                                primary: { variant: 'subtitle2' },
                                secondary: { variant: 'caption' }
                            }}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </>
    )
}

export default FeaturesMenu
